import React, { useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getDefaultContest } from '../../ui/builders/utils';

const isStudentInIcodeContestGrades = (contest, userDetails) => {
    if (!contest) return false;
    const fromC = Number(contest.fromClass);
    const toC = Number(contest.toClass);
    const grade = Number(userDetails?.grade);
    return grade >= fromC && grade <= toC;
};

const Icode = () => {

    const iframeRef = useRef(null);
    const navigate = useNavigate();
    const { userDetails, isAuth, isStudent, initAuthDetails } = useSelector(state => state.auth);
    const { activeContests } = useSelector(state => state.contest);
    const { texts, lang } = useSelector(state => state.translate);

    const icodeContest = useMemo(() => {
        const id = Number(window.config?.icodeContestId);
        if (!Number.isFinite(id)) return undefined;
        return activeContests.find((c) => Number(c.id) === id);
    }, [activeContests]);

    const isEligible = useMemo(() => {
        if (!icodeContest) return false;
        if (!isStudent) return true;
        return isStudentInIcodeContestGrades(icodeContest, userDetails);
    }, [icodeContest, isStudent, userDetails]);

    const studentBlockedAlertRef = useRef(false);

    useEffect(() => {
        if (isAuth === false) {
            const fallbackId = getDefaultContest(activeContests)?.id ?? 14000;
            navigate(`/${fallbackId}`, { replace: true });
            return;
        }
        if (initAuthDetails !== true || !activeContests.length || Object.keys(texts).length === 0) {
            return;
        }

        if (isStudent && icodeContest && !isStudentInIcodeContestGrades(icodeContest, userDetails)) {
            if (!studentBlockedAlertRef.current) {
                studentBlockedAlertRef.current = true;
                const fromC = icodeContest.fromClass;
                const toC = icodeContest.toClass;
                const template = texts['icode.studentGradeNotInContestAlert'];
                const message = String(template).replace('{fromClass}', String(fromC)).replace('{toClass}', String(toC))

                window.alert(message);
            }
            const fallbackId = getDefaultContest(activeContests)?.id ?? 14000;
            navigate(`/${fallbackId}`, { replace: true });
        }
    }, [isAuth, activeContests, icodeContest, isStudent, userDetails, navigate, texts]);

    useEffect(() => {
        const loadIframeContent = () => {
            if (iframeRef.current) {
                // Dynamically create the form within the iframe
                const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
                const form = iframeDoc.createElement("form");
                form.method = "POST";
                form.action = `${window.config.baseApiUrl}/auth/authorize`; // Replace with your API endpoint



                const params = {
                    response_type: "id_token",
                    client_id: window.config.icodeClientId,
                    redirect_uri: window.config.icodeRedirectUri,
                    scope: "profile",
                    nonce: "random_nonce",
                    prompt: "none",
                    state: JSON.stringify({
                        providerLink: window.config.icodeProviderLink,
                        lang: lang ?? "he",
                        gender: userDetails?.genderId ?? 1,
                        // supervisionId: supervisionId ?? 1,
                        // educationTypeId: educationTypeId ?? 1,
                        class: userDetails?.grade ?? 1
                    }),
                };

                for (const [key, value] of Object.entries(params)) {
                    const input = iframeDoc.createElement("input");
                    input.type = "hidden";
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                }

                // Append the form to the iframe's body and submit it
                iframeDoc.body.appendChild(form);
                form.submit(); // Redirect happens within the iframe
            }
        };
        if (userDetails && lang)
            loadIframeContent();
        // eslint-disable-next-line 
    }, [userDetails, lang]);


    useEffect(() => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { action: "langChange", lang: lang }, '*'
            );
        }
    }, [lang]);

    if (isAuth === false) {
        return null;
    }
    if (isAuth === true && activeContests.length > 0 && !isEligible) {
        return null;
    }

    return (
        <div>
            <iframe
                ref={iframeRef}
                frameBorder={0}
                title={texts.albertTitle}
                style={{ width: "100%", height: "100%", position: "absolute" }}
                allow="microphone *"
            ></iframe>
        </div>
    );
};

export default Icode;
